import React from 'react';
import { ArrowUpRightIcon, BadgeCheckIcon, BriefcaseIcon, TrendingUpIcon, WorkflowIcon } from 'lucide-react';
import { useHRStore } from '../../hooks/useHRStore';
import type { Navigate, RoleSegment } from '../../types/hr';
import type { IconComponent } from '../../types/ui';
import { ROLE_SEGMENTS, TEAMS_BY_GROUP, teamFor } from '../../utils/teams';
import type { WorkGroup } from '../../utils/teams';
const ICONS: Record<RoleSegment, IconComponent> = {
  'loan-officers': BriefcaseIcon,
  operations: WorkflowIcon,
  sales: TrendingUpIcon,
  licensed: BadgeCheckIcon
};
const ACCENTS: Record<RoleSegment, string> = {
  'loan-officers': 'bg-flame/10 text-flame',
  operations: 'bg-navy/[0.06] text-navy',
  sales: 'bg-azure/10 text-azure',
  licensed: 'bg-deep/10 text-deep'
};
const GROUP_FOR_SEGMENT: Partial<Record<RoleSegment, WorkGroup>> = {
  operations: 'Operations',
  sales: 'Sales'
};
export function RoleMetrics({
  onNavigate
}: {
  onNavigate: Navigate;
}) {
  const {
    employees
  } = useHRStore();
  return <section aria-labelledby="role-metrics-title" data-id="element-82" data-mp="21g8fqqvgyhvk">
      <div className="mb-4 flex items-end justify-between" data-id="element-83" data-mp="26g4kwtfwkfcp">
        <div data-id="element-84" data-mp="26ejtvdssrejl">
          <h2 id="role-metrics-title" className="text-[15px] font-semibold text-navy" data-id="element-85" data-mp="26i7ka42y3ku3">
            People by role
          </h2>
          <p className="text-xs text-muted" data-id="element-86" data-mp="2ge0tbdhggw74">Click a card to open the filtered employee list</p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4" data-id="element-87" data-mp="1irbnesjmtzfz">
        {ROLE_SEGMENTS.map(s => {
        const Icon = ICONS[s.id];
        const members = employees.filter(s.test);
        const group = GROUP_FOR_SEGMENT[s.id];
        const teams = group ? TEAMS_BY_GROUP[group].map(t => ({
          team: t,
          count: members.filter(e => teamFor(e) === t).length
        })).filter(t => t.count > 0) : [];
        const max = Math.max(1, ...teams.map(t => t.count));
        return <button key={s.id} type="button" onClick={() => onNavigate({
          page: 'employees',
          segment: s.id
        })} className="group flex flex-col rounded-2xl bg-white p-5 text-left shadow-soft ring-1 ring-slate-900/[0.04] transition hover:-translate-y-0.5 hover:shadow-lift" data-id="element-88" data-mp="14y3umxjtexwp">
              <span className="flex items-start justify-between" data-id="element-89" data-mp="16qbwv57m0f4p">
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${ACCENTS[s.id]}`} data-id="element-90" data-mp="11qh8b8bb41mt">
                  <Icon className="h-5 w-5" aria-hidden="true" data-id="element-91" data-mp="e03og52vl93b" />
                </span>
                <ArrowUpRightIcon className="h-4 w-4 text-slate-300 transition group-hover:text-flame" aria-hidden="true" data-id="element-92" data-mp="91sfdwken2m5" />
              </span>
              <span className="mt-5 text-[32px] font-semibold leading-none tracking-tight text-navy" data-id="element-93" data-mp="nvev0044r9zz">{members.length}</span>
              <span className="mt-2 text-sm font-medium text-slate-700" data-id="element-94" data-mp="1zcew9l8ggz65">{s.label}</span>
              <span className="mt-0.5 text-xs text-muted" data-id="element-95" data-mp="1udts5aq31vcf">{s.description}</span>
              {teams.length > 0 && <span className="mt-4 block w-full border-t border-slate-100 pt-3" data-id="element-96" data-mp="21wtadd1t5gu7">
                  <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted" data-id="element-97" data-mp="26w60q7rw161j">By team</span>
                  <span className="mt-2 block space-y-2" data-id="element-98" data-mp="1sxg38r15p4cn">
                    {teams.map(t => <span key={t.team} className="block" data-id="element-99" data-mp="1wxhv68h3ajeq">
                        <span className="flex items-center justify-between text-xs" data-id="element-100" data-mp="1x02v2rxtup9u">
                          <span className="text-slate-600" data-id="element-101" data-mp="2kpx8pbpz0hv5">{t.team}</span>
                          <span className="font-semibold text-navy" data-id="element-102" data-mp="2pmfb44xtw44b">{t.count}</span>
                        </span>
                        <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-canvas" data-id="element-103" data-mp="2bva7ysv94tp8">
                          <span className={`block h-full rounded-full ${s.id === 'sales' ? 'bg-azure' : 'bg-navy'}`} style={{
                    width: `${t.count / max * 100}%`
                  }} data-id="element-104" data-mp="2fvayvczjexd6" />
                        </span>
                      </span>)}
                  </span>
                </span>}
            </button>;
      })}
      </div>
    </section>;
}